import { useEffect, useRef, useState } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { CheckIcon } from './Icons';
import { ImageWithFallback } from './ImageWithFallback';
import { QuantityStepper } from './QuantityStepper';
import { StoreProductDetail } from '../../models';
import { formatCurrency } from '../../utils/currency';
import { safeImageUrl, thumbnailUrlOf } from '../../utils/url';
import { productPlaceholderIcon } from '../../utils/productPlaceholder';
import { getVariationLabel } from '../../utils/variantResolution';
import { useVariantSelection } from '../../hooks/useVariantSelection';
import { ColorSwatchButton } from '../product/ColorSwatchButton';
import { SizeChipButton } from '../product/SizeChipButton';
import { useToastStore } from '../../store/toastStore';

const ADDED_FEEDBACK_MS = 1200;

interface QuickAddModalProps {
  product: StoreProductDetail | null;
  onClose: () => void;
}

export function QuickAddModal({ product, onClose }: QuickAddModalProps) {
  const {
    hasVariants,
    uniqueColors,
    uniqueValues,
    selectedColor,
    selectedValue,
    quantity,
    resolvedVariant,
    inCartQuantity,
    availableToAdd,
    reachedCartLimit,
    isOutOfStock,
    canAddToCart,
    selectColor,
    selectValue,
    increaseQuantity,
    decreaseQuantity,
    addToCart,
  } = useVariantSelection(product);

  const showToast = useToastStore((s) => s.show);
  const [added, setAdded] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    setAdded(false);
    return () => clearTimeout(closeTimer.current);
  }, [product]);

  const handleAdd = () => {
    if (added) return;
    const result = addToCart();
    if (result === 'max_stock') {
      showToast('Você já tem na sacola toda a quantidade disponível em estoque.', 'error');
      return;
    }
    if (result !== 'added') return;
    setAdded(true);
    closeTimer.current = setTimeout(onClose, ADDED_FEEDBACK_MS);
  };

  const actionButton = (
    <Button fullWidth size="lg" variant="primary" onClick={handleAdd} disabled={!added && !canAddToCart}>
      {added ? (
        <>
          <CheckIcon className="w-4 h-4" />
          Adicionado à sacola
        </>
      ) : isOutOfStock ? (
        'Esgotado'
      ) : reachedCartLimit ? (
        'Limite do estoque na sacola'
      ) : (
        'Adicionar à sacola'
      )}
    </Button>
  );

  const displayPrice = product ? product.promotionalPrice ?? product.price : null;
  const thumbnail = thumbnailUrlOf(product?.images[0]);

  return (
    <Modal open={!!product} onClose={onClose} title="Adicionar à sacola" footer={product ? actionButton : undefined}>
      {product && (
        <>
          <div className="flex items-center gap-3 mb-4">
            <ImageWithFallback
              src={thumbnail ? safeImageUrl(thumbnail) : undefined}
              alt={product.name}
              className="w-14 h-14 rounded-lg flex-shrink-0"
              icon={<img src={productPlaceholderIcon(product.id)} alt="" className="w-10 h-10 opacity-70" />}
            />
            <div className="min-w-0">
              <p className="font-display font-semibold text-text-dark text-sm leading-snug truncate">
                {product.name}
              </p>
              {displayPrice !== null && displayPrice !== undefined && (
                <p className="text-chocolate-dark font-bold text-sm mt-0.5">R$ {formatCurrency(displayPrice)}</p>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4">
            {uniqueColors.length > 0 && (
              <div>
                <p className="text-sm font-medium text-text-medium mb-2">
                  Cor{selectedColor && <span className="text-text-medium font-medium"> — {selectedColor.name}</span>}
                </p>
                <div className="flex flex-wrap gap-x-1 gap-y-0.5 -ml-1">
                  {uniqueColors.map((color) => {
                    const outOfStock = !product.variants.some(
                      (v) => v.stockQuantity > 0 && v.color?.id === color.id
                    );
                    return (
                      <ColorSwatchButton
                        key={color.id}
                        color={color}
                        outOfStock={outOfStock}
                        selected={selectedColor?.id === color.id}
                        onSelect={() => selectColor(color)}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {hasVariants && uniqueValues.length > 0 && (
              <div>
                <p className="text-sm font-medium text-text-medium mb-2">
                  {getVariationLabel(product.variants[0]?.variationTypeId)}
                </p>
                <div className="flex flex-wrap gap-2">
                  {uniqueValues.map((val) => {
                    const variant = product.variants.find(
                      (v) => v.variationValue === val && (!selectedColor || v.color?.id === selectedColor.id)
                    );
                    const outOfStock = variant ? variant.stockQuantity === 0 : false;
                    const label = (variant ?? product.variants.find((v) => v.variationValue === val))?.label ?? val;
                    return (
                      <SizeChipButton
                        key={val}
                        label={label}
                        soldOut={outOfStock}
                        selected={selectedValue === val}
                        onSelect={() => selectValue(val)}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {(!hasVariants || resolvedVariant) && (
              <div>
                <QuantityStepper
                  quantity={quantity}
                  onDecrease={decreaseQuantity}
                  onIncrease={increaseQuantity}
                  disableDecrease={quantity <= 1}
                  disableIncrease={hasVariants && quantity >= availableToAdd}
                  size="lg"
                />
                {inCartQuantity > 0 && !added && (
                  <p className={`mt-2 text-sm ${reachedCartLimit ? 'text-danger' : 'text-text-medium'}`} role="status">
                    {reachedCartLimit
                      ? `Você já tem ${inCartQuantity} na sacola, que é todo o estoque disponível.`
                      : `Você já tem ${inCartQuantity} na sacola. Dá para adicionar mais ${availableToAdd}.`}
                  </p>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </Modal>
  );
}
